import React, { useCallback } from 'react';

import {
  AdminFonctionnelControllerApi,
  AutoriteFourriereDto,
  AutoriteFourriereRequestDto,
  ConditionsReactivateAutoriteFourriereDto,
  ReferentielSearchResultDtoAutoriteFourriereDto,
  ReferentielUsesDto,
} from 'lib_api/lib/api/gen';

import {
  FetchDataResult,
  TableHeaderFunctionWithoutSort,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { AutoriteFourriereSortOptions } from 'enums/referentiels/AutoriteFourriere';
import { useApi } from 'hooks/ApiStoreContext';
import { searchAutoriteFourriereUsingSpecs } from 'search/searchAutoriteFourriere';
import { FilterDashboardAutoriteFourriere } from 'types/referentiels/AutoriteFourriere';

import DashboardHeader from './DashboardHeader/DashboardHeader';
import { AutoriteFourriereFormValues } from './type';

export const buildHeader: TableHeaderFunctionWithoutSort<
  AutoriteFourriereDto,
  FilterDashboardAutoriteFourriere
> = (fetchedData, _selectedData, filter, setFilter) => (
  <DashboardHeader
    totalCount={fetchedData !== undefined ? fetchedData.total : 0}
    filter={filter}
    setFilter={setFilter}
  />
);

/**
 * Hook sent to generic search hook to send search request and convert returned result to readable data
 */
export function useSearchAutoriteFourriere(
  filters: FilterDashboardAutoriteFourriere,
  sorts: TableSort<AutoriteFourriereSortOptions>,
  pagination: TablePagination,
): [
  () => Promise<ReferentielSearchResultDtoAutoriteFourriereDto>,
  (
    result: ReferentielSearchResultDtoAutoriteFourriereDto,
  ) => FetchDataResult<AutoriteFourriereDto>,
] {
  const controller = useApi().ReferentielControllerApi;

  return [
    () =>
      searchAutoriteFourriereUsingSpecs(controller, filters, sorts, pagination),
    result => {
      return {
        data: result.results.referenceDtoList,
        total: result.total,
      };
    },
  ];
}

export function useChangeValidityAutoriteFourriere(
  controller: AdminFonctionnelControllerApi,
  currentCommentaires: string | null,
): [
  (idCorrelation: string) => Promise<Response>,
  (idCorrelation: string) => Promise<Response>,
  (idCorrelation: string) => Promise<ConditionsReactivateAutoriteFourriereDto>,
  (idCorrelation: string) => Promise<ReferentielUsesDto>,
] {
  const submitDeprecateReferentiel = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.deprecateAutoriteFourriereUsingPUT(idCorrelation, {
        commentaires: currentCommentaires === '' ? null : currentCommentaires,
      });
    },
    [controller, currentCommentaires],
  );

  const submitReactivateReferentiel = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.reactivateAutoriteFourriereUsingPUT(idCorrelation, {
        commentaires: currentCommentaires === '' ? null : currentCommentaires,
      });
    },
    [controller, currentCommentaires],
  );

  const conditionsReactivateReferentiel = useCallback(
    (
      idCorrelation: string,
    ): Promise<ConditionsReactivateAutoriteFourriereDto> => {
      return controller.conditionReactivationAutoriteFourriereUsingGET(
        idCorrelation,
      );
    },
    [controller],
  );

  const getUsesRequest = useCallback(
    (idCorrelation: string): Promise<ReferentielUsesDto> => {
      return controller.searchUsesAutoriteFourriereUsingGET(idCorrelation);
    },
    [controller],
  );

  return [
    submitReactivateReferentiel,
    submitDeprecateReferentiel,
    conditionsReactivateReferentiel,
    getUsesRequest,
  ];
}

export const buildFormValues = (
  autorite: AutoriteFourriereDto,
): AutoriteFourriereFormValues => {
  return {
    numeroDepartement: autorite.numeroDepartement,
    libelleDepartement: autorite.libelleDepartement,
    insee: autorite.codeInsee ?? undefined,
    idCorrelationType: autorite.type.idCorrelation,
    libelle: autorite.libelleComplet,
    adresse: autorite.voie,
    codePostal: autorite.codePostal,
    ville: autorite.ville,
    numeroVoie: autorite.numeroVoie ?? undefined,
    complement: autorite.complementAdresse ?? undefined,
    cedex: autorite.cedex ?? undefined,
    commentaires: autorite.commentaires ?? undefined,
    idAlienation: autorite.idAlienation ?? undefined,
    idAlienationUtilisateur: autorite.idAlienationUtilisateur ?? undefined,
  };
};

export const buildRequestDto = (
  values: AutoriteFourriereFormValues,
): AutoriteFourriereRequestDto => {
  return {
    numeroDepartement: values.numeroDepartement || null,
    libelleDepartement: values.libelleDepartement || null,
    libelle: values.libelle || null,
    codePostal: values.codePostal || null,
    numeroVoie: values.numeroVoie || null,
    voie: values.adresse || null,
    ville: values.ville || null,
    insee: values.insee || null,
    complement: values.complement || null,
    cedex: values.cedex || null,
    idCorrelationType: values?.idCorrelationType || null,
    idAlienation: values.idAlienation || null,
    idAlienationUtilisateur: values.idAlienationUtilisateur || null,
    commentaires: values.commentaires || null,
  };
};
